import React, { useEffect, useRef, useState } from 'react';
import { DocumentArrowDownIcon, CheckIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { jsPDF } from 'jspdf';
import { EDashboard } from '../../types';
import {
  captureElementAsPng,
  getDashboardElement,
  generateFileName
} from '../../utils/export-helpers';

interface ExportPdfButtonProps {
  activeDashboard: EDashboard;
  projectName?: string;
}

type ExportStatus = 'idle' | 'exporting' | 'done' | 'error';

export const ExportPdfButton: React.FC<ExportPdfButtonProps> = ({
  activeDashboard,
  projectName
}) => {
  const [status, setStatus] = useState<ExportStatus>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const settle = (result: 'done' | 'error') => {
    setStatus(result);
    timerRef.current = setTimeout(() => setStatus('idle'), 2500);
  };

  const handleExport = async () => {
    if (status === 'exporting') return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus('exporting');

    try {
      const dashboardElement = getDashboardElement();

      if (!dashboardElement) {
        console.error('Dashboard content element not found');
        settle('error');
        return;
      }

      const dataUrl = await captureElementAsPng(dashboardElement);
      
      const img = new Image();
      img.src = dataUrl;
      
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
      });
      
      // Convert pixel dimensions to mm
      // Using a scale that produces reasonable PDF sizes
      // Since we capture at 2x pixel ratio, we'll use a scale that results in good print quality
      const pixelsToMm = (pixels: number) => {
        // Base scale: treat the high-res capture as ~150 DPI for print
        // This gives us good quality without massive PDF dimensions
        return (pixels * 25.4) / 150;
      };
      
      let imgWidthMm = pixelsToMm(img.width);
      let imgHeightMm = pixelsToMm(img.height);
      
      // Cap maximum size to A3 dimensions (420mm x 297mm) for practical reasons
      const maxWidthMm = 420;
      const maxHeightMm = 297;
      
      // Scale down if needed while maintaining aspect ratio
      if (imgWidthMm > maxWidthMm || imgHeightMm > maxHeightMm) {
        const scaleX = maxWidthMm / imgWidthMm;
        const scaleY = maxHeightMm / imgHeightMm;
        const scale = Math.min(scaleX, scaleY);
        
        imgWidthMm *= scale;
        imgHeightMm *= scale;
      }
      
      // Determine orientation based on aspect ratio
      const orientation = img.width > img.height ? 'landscape' : 'portrait';
      
      const pdf = new jsPDF({
        orientation: orientation,
        unit: 'mm',
        format: [imgWidthMm, imgHeightMm]  // Dynamic dimensions based on image
      });
      
      // Add image to fill the entire page (no margins, perfect fit)
      pdf.addImage(dataUrl, 'PNG', 0, 0, imgWidthMm, imgHeightMm);
      
      const filename = generateFileName(projectName, activeDashboard, 'pdf');
      pdf.save(filename);
      settle('done');
    } catch (error) {
      console.error('Error exporting dashboard as PDF:', error);
      settle('error');
    }
  };

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={status === 'exporting'}
      className="btn-icon flex items-center justify-center gap-2 min-w-[8.5rem]"
      title={status === 'error' ? 'Failed to export dashboard as PDF. Try again.' : 'Export dashboard as PDF'}
    >
      {status === 'done' ? (
        <CheckIcon className="h-4 w-4 text-primary" />
      ) : status === 'error' ? (
        <ExclamationCircleIcon className="h-4 w-4 text-red-400" />
      ) : (
        <DocumentArrowDownIcon className="h-4 w-4" />
      )}
      {status === 'exporting'
        ? 'Exporting...'
        : status === 'done'
        ? 'Exported'
        : status === 'error'
        ? 'Export failed'
        : 'Export PDF'}
    </button>
  );
};